import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Classification } from '../types/Types';
import axios from 'axios';
import { CLASSIFICATION_TYPE_ROUTE_MAP } from '../constants/classificationTypes';

interface ClassificationCacheContextType {
  // Cache management
  getClassification: (id: string) => Classification | null;
  getAllClassifications: () => Classification[];
  preloadClassificationsForRows: (rows: any[]) => Promise<void>;
  loadAllActiveClassificationsByType: (type: "primary" | "secondary" | "tertiary" | "quaternary" | "fifth" | "sixth" | "production-department" | "collection" | "classification" | "raw-material-type" | "pa-type" | "sa-type" | "mp-pp-type" | "partner-type") => Promise<void>;
  isLoading: boolean;

  // Search functionality
  searchClassifications: (query: string, type?: "primary" | "secondary" | "tertiary" | "quaternary" | "fifth" | "sixth" | "production-department" | "collection" | "classification" | "raw-material-type" | "pa-type" | "sa-type" | "mp-pp-type" | "partner-type") => Classification[];

  // Initialize with getClassificationById function
  initialize: (getClassificationByIdFn: (id: string) => Promise<any>) => void;
}

const ClassificationCacheContext = createContext<ClassificationCacheContextType | undefined>(undefined);

const TYPE_ROUTE_MAP = CLASSIFICATION_TYPE_ROUTE_MAP;

// Canonical type keys only — TYPE_ROUTE_MAP also holds legacy aliases ('primary',
// 'secondary', …) that point at the same routes. Refetching per alias would fire the
// same request several times for one write.
const CANONICAL_TYPES = [
  'production-department', 'collection', 'classification',
  'raw-material-type', 'pa-type', 'sa-type', 'mp-pp-type', 'partner-type',
] as const;

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const ClassificationCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [classificationCache, setClassificationCache] = useState<Map<string, Classification>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [getClassificationByIdFn, setGetClassificationByIdFn] = useState<((id: string) => Promise<any>) | null>(null);
  // Stale-while-revalidate: existing cache is served immediately for instant
  // paint; every load call fires a fresh refetch per type. inflightByType
  // collapses N concurrent callers for the same type to exactly 1 request.
  const inflightByType = useRef<Map<string, Promise<void>>>(new Map());
  // Individual classification IDs whose per-row fetch has already failed once
  // this mount. Prevents a permanently-missing classification from being
  // re-fetched (and re-flashing loading state) on every rows/effect re-run.
  const failedIdsRef = useRef<Set<string>>(new Set());

  const initialize = useCallback((getClassificationByIdFn: (id: string) => Promise<any>) => {
    setGetClassificationByIdFn(() => getClassificationByIdFn);
  }, []);

  const getClassification = useCallback((id: string): Classification | null => {
    return classificationCache.get(id) || null;
  }, [classificationCache]);

  const getAllClassifications = useCallback((): Classification[] => {
    return Array.from(classificationCache.values());
  }, [classificationCache]);

  const searchClassifications = useCallback((query: string, type?: "primary" | "secondary" | "tertiary" | "quaternary" | "fifth" | "sixth" | "production-department" | "collection" | "classification" | "raw-material-type" | "pa-type" | "sa-type" | "mp-pp-type" | "partner-type"): Classification[] => {
    let classifications = Array.from(classificationCache.values());
    
    // Filter by type if specified
    if (type) {
      classifications = classifications.filter(c => c.type === type);
    }
    
    if (!query.trim()) return classifications.slice(0, 50); // Limit initial results

    const lowerQuery = query.toLowerCase();
    return classifications.filter(classification => 
      classification.code?.toLowerCase().includes(lowerQuery) ||
      classification.name?.toLowerCase().includes(lowerQuery) ||
      classification.nameEN?.toLowerCase().includes(lowerQuery) ||
      `${classification.code} ${classification.name}`.toLowerCase().includes(lowerQuery)
    ).slice(0, 50); // Limit search results
  }, [classificationCache]);

  const loadAllActiveClassificationsByType = useCallback(async (type: "primary" | "secondary" | "tertiary" | "quaternary" | "fifth" | "sixth" | "production-department" | "collection" | "classification" | "raw-material-type" | "pa-type" | "sa-type" | "mp-pp-type" | "partner-type") => {
    // Existing cache for this type is served immediately (instant paint) while
    // a fresh refetch is always fired. inflightByType deduplicates concurrent
    // callers for the same type so only 1 request goes out at a time per type.
    if (inflightByType.current.has(type)) return inflightByType.current.get(type)!;

    const doFetch = async () => {
      setIsLoading(true);

      try {
        const apiUrl = process.env.REACT_APP_API_URL ?? '';
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Authentication token missing');

        // Load all active classifications for the specific type in batches
        let page = 1;
        const limit = 100; // Larger batches for initial load
        let hasMore = true;
        const allClassifications: Classification[] = [];

        const typeBaseRoute = TYPE_ROUTE_MAP[type];

        while (hasMore) {
          const response = await axios.get(
            `${apiUrl}${typeBaseRoute}/active`,
            {
            headers: { 'x-auth-token': token },
            params: {
              search: '', // Empty search to get all
              sort: 'createdAt',
              order: 'desc',
              page,
              limit,
            },
          });

          const { classifications, totalPages } = response.data;
          allClassifications.push(...classifications);

          hasMore = page < totalPages;
          page++;

          // Add small delay between requests to avoid rate limiting
          if (hasMore) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }

        // Replace this type's entries in the cache with fresh data
        if (allClassifications.length > 0) {
          setClassificationCache(prev => {
            const newCache = new Map(prev);
            allClassifications.forEach(classification => {
              newCache.set(classification._id, classification);
            });
            return newCache;
          });
        }

      } catch {
        // Error loading classifications silently handled — existing cache remains
      } finally {
        setIsLoading(false);
        inflightByType.current.delete(type);
      }
    };

    const promise = doFetch();
    inflightByType.current.set(type, promise);
    return promise;
  }, []);

  // Auto-invalidate on write — the half this cache was missing.
  //
  // `loadAllActiveClassificationsByType` only refetches when a consumer mounts. Under the
  // Open Tabs keep-alive bar a screen like /items stays MOUNTED, so after editing a codebook
  // on /classification/<type> nothing ever re-triggered that mount effect and the new record
  // stayed invisible until F5. This mirrors what createCodebookCache does for the newer
  // dropdowns: any successful write to a classification route invalidates and refetches that
  // type, and because the cache lives in provider state every mounted consumer re-renders.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use((response) => {
      const method = (response.config.method || 'get').toLowerCase();
      if (method === 'post' || method === 'put' || method === 'patch' || method === 'delete') {
        const url = response.config.url || '';
        CANONICAL_TYPES.forEach((type) => {
          const route = TYPE_ROUTE_MAP[type];
          if (!route) return;
          // Path-boundary match so '/api/classification-pa-type' can't match '…-pa-type-x'.
          if (!new RegExp(escapeRegex(route) + '(/|\\?|#|$)').test(url)) return;
          // A read already in flight was started BEFORE this write landed, so its result is
          // stale by definition — drop the guard or the refetch below would be swallowed.
          inflightByType.current.delete(type);
          loadAllActiveClassificationsByType(type);
        });
      }
      return response;
    });
    return () => { axios.interceptors.response.eject(interceptorId); };
  }, [loadAllActiveClassificationsByType]);

  const preloadClassificationsForRows = useCallback(async (rows: any[]) => {
    if (!getClassificationByIdFn || !rows?.length) return;

    // Extract unique classification IDs that aren't cached and haven't
    // already failed to load this mount (background fill — does not touch
    // the global isLoading flag, which is reserved for full-type loads).
    const uncachedIds = new Set<string>();
    rows.forEach(row => {
      const id = row.classification?._id;
      if (id && !classificationCache.has(id) && !failedIdsRef.current.has(id)) {
        uncachedIds.add(id);
      }
    });

    if (uncachedIds.size === 0) return;

    try {
      // Load in smaller batches to avoid overwhelming the API
      const batchSize = 3;
      const idsArray = Array.from(uncachedIds);
      const newClassifications: Classification[] = [];

      for (let i = 0; i < idsArray.length; i += batchSize) {
        const batch = idsArray.slice(i, i + batchSize);
        const batchPromises = batch.map(async (id) => {
          try {
            const classification = await getClassificationByIdFn(id);
            if (classification) {
              failedIdsRef.current.delete(id);
            } else {
              failedIdsRef.current.add(id);
            }
            return classification;
          } catch (error) {
            // Failed to load individual classification — remember so we
            // don't retry it forever and re-flash the overlay
            failedIdsRef.current.add(id);
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(classification => {
          if (classification) newClassifications.push(classification);
        });

        // Add small delay between batches to avoid API rate limiting
        if (i + batchSize < idsArray.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Update cache
      if (newClassifications.length > 0) {
        setClassificationCache(prev => {
          const newCache = new Map(prev);
          newClassifications.forEach(classification => {
            newCache.set(classification._id, classification);
          });
          return newCache;
        });
      }

    } catch (error) {
      // Error preloading classifications silently handled
    }
  }, [classificationCache, getClassificationByIdFn]);

  const value: ClassificationCacheContextType = useMemo(() => ({
    getClassification,
    getAllClassifications,
    preloadClassificationsForRows,
    loadAllActiveClassificationsByType,
    isLoading,
    searchClassifications,
    initialize,
  }), [
    getClassification,
    getAllClassifications,
    preloadClassificationsForRows,
    loadAllActiveClassificationsByType,
    isLoading,
    searchClassifications,
    initialize,
  ]);

  return <ClassificationCacheContext.Provider value={value}>{children}</ClassificationCacheContext.Provider>;
};

export const useClassificationCache = (): ClassificationCacheContextType => {
  const context = useContext(ClassificationCacheContext);
  if (!context) {
    throw new Error('useClassificationCache must be used within ClassificationCacheProvider');
  }
  return context;
};